/**
 * Settings: the knobs, the tree, the tags, the calendar feed, export, and the
 * password.
 *
 * Everything here is rare-use, so it favours being obvious over being compact:
 * each section says what it affects and what the consequence of changing it is.
 */

import { useCallback, useEffect, useState } from 'react'

import { ApiError, api } from '@/api/client'
import type { Category, TagWithCount } from '@/api/types'
import { CalendarFeeds } from '@/components/CalendarFeeds'
import { useToast } from '@/components/Toast'
import { ErrorState, LoadingBlock } from '@/components/ui'
import { effectiveCategoryColor } from '@/lib/color'
import { pluralize } from '@/lib/format'

interface AppSettings {
  dashboard_item_limit: number
  escalation_threshold_days: number
  timezone: string
  ics_url: string

  invoice_from_name: string
  invoice_from_address: string
  invoice_short_name: string
  invoice_default_rate: string
  invoice_payment_terms: string
  invoice_beneficiary_name: string
  invoice_account_number: string
  invoice_routing_number: string
  invoice_bank_name: string
}

/** The invoice fields, in the order they appear on the printed page. */
const INVOICE_FIELDS: {
  key: keyof AppSettings
  label: string
  hint?: string
  multiline?: boolean
}[] = [
  { key: 'invoice_from_name', label: 'Your name', hint: 'Bold, at the top of the page' },
  {
    key: 'invoice_from_address',
    label: 'Your address',
    hint: 'One line per line you type',
    multiline: true,
  },
  {
    key: 'invoice_short_name',
    label: 'Short name',
    hint: 'Only names the file: "Invoice - Josh Lee - 2026.8.31.docx"',
  },
  {
    key: 'invoice_default_rate',
    label: 'Default hourly rate',
    hint: 'What a client bills at when it has no rate of its own',
  },
  { key: 'invoice_payment_terms', label: 'Payment terms', multiline: true },
  { key: 'invoice_beneficiary_name', label: 'Beneficiary name' },
  { key: 'invoice_account_number', label: 'Account number' },
  { key: 'invoice_routing_number', label: 'ACH routing number' },
  { key: 'invoice_bank_name', label: 'Bank name' },
]

/** The zones this user plausibly needs, plus whatever is already set. */
const COMMON_ZONES = [
  'America/Denver',
  'America/Los_Angeles',
  'America/Chicago',
  'America/New_York',
  'America/Phoenix',
  'UTC',
]

function Section({
  title,
  description,
  children,
}: {
  title: string
  description?: string
  children: React.ReactNode
}) {
  return (
    <section className="card p-4">
      <h2 className="text-sm font-semibold">{title}</h2>
      {description && <p className="mt-0.5 mb-3 text-xs text-ink-3">{description}</p>}
      <div className={description ? '' : 'mt-3'}>{children}</div>
    </section>
  )
}

export function SettingsPage() {
  const toast = useToast()
  const [settings, setSettings] = useState<AppSettings | null>(null)
  const [categories, setCategories] = useState<Category[]>([])
  const [tags, setTags] = useState<TagWithCount[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const [currentPassword, setCurrentPassword] = useState('')
  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [mergeSource, setMergeSource] = useState('')
  const [mergeTarget, setMergeTarget] = useState('')

  const load = useCallback(async () => {
    try {
      const [nextSettings, nextCategories, nextTags] = await Promise.all([
        api.settings.read(),
        api.categories.list(true),
        api.tags.list(),
      ])
      setSettings(nextSettings)
      setCategories(nextCategories)
      setTags(nextTags)
      setError(null)
    } catch (caught) {
      if (caught instanceof ApiError && !caught.isUnauthorized) setError(caught.message)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    void load()
  }, [load])

  async function save(patch: Partial<AppSettings>) {
    try {
      setSettings(await api.settings.update(patch))
      toast.success('Saved.')
    } catch (caught) {
      toast.error(caught instanceof ApiError ? caught.message : 'Could not save that.')
      await load()
    }
  }

  async function act(action: () => Promise<unknown>, success: string) {
    try {
      await action()
      await load()
      toast.success(success)
    } catch (caught) {
      toast.error(caught instanceof ApiError ? caught.message : 'That did not work.')
    }
  }

  if (loading) return <LoadingBlock label="Loading settings" />
  if (error || !settings) {
    return <ErrorState message={error ?? 'Could not load settings.'} onRetry={() => void load()} />
  }

  const zones = COMMON_ZONES.includes(settings.timezone)
    ? COMMON_ZONES
    : [settings.timezone, ...COMMON_ZONES]

  return (
    <div className="flex flex-col gap-4">
      <h1 className="px-1 text-lg font-bold">Settings</h1>

      {/* ---------------------------------------------------------- knobs --*/}
      <Section
        title="Dashboard"
        description="How much the home screen shows before you ask for the rest."
      >
        <div className="flex flex-wrap items-end gap-4">
          <label className="text-xs font-medium text-ink-2">
            Items before &ldquo;Show all&rdquo;
            <input
              type="number"
              min={1}
              max={200}
              className="field mt-1 w-24"
              defaultValue={settings.dashboard_item_limit}
              onBlur={(event) => {
                const value = Number(event.target.value)
                if (value !== settings.dashboard_item_limit && value >= 1) {
                  void save({ dashboard_item_limit: value })
                }
              }}
            />
          </label>

          <label className="text-xs font-medium text-ink-2">
            Escalate when due within (days)
            <input
              type="number"
              min={0}
              max={90}
              className="field mt-1 w-24"
              defaultValue={settings.escalation_threshold_days}
              onBlur={(event) => {
                const value = Number(event.target.value)
                if (value !== settings.escalation_threshold_days && value >= 0) {
                  void save({ escalation_threshold_days: value })
                }
              }}
            />
          </label>

          <label className="text-xs font-medium text-ink-2">
            Timezone
            <select
              className="field mt-1 w-48"
              value={settings.timezone}
              onChange={(event) => void save({ timezone: event.target.value })}
            >
              {zones.map((zone) => (
                <option key={zone} value={zone}>
                  {zone}
                </option>
              ))}
            </select>
          </label>
        </div>
        <p className="mt-2 text-xs text-ink-3">
          The timezone decides what counts as today, and therefore what counts as overdue.
        </p>
      </Section>

      {/* ------------------------------------------------------- calendar --*/}
      <Section
        title="Outlook calendar"
        description="Subscribe once and your due dates stay in sync. This URL is the credential — anyone who has it can read your due dates."
      >
        <div className="flex flex-wrap items-center gap-2">
          <input
            readOnly
            className="field flex-1 font-mono text-xs"
            value={settings.ics_url}
            onFocus={(event) => event.target.select()}
            aria-label="Calendar feed URL"
          />
          <button
            type="button"
            className="btn btn-neutral btn-sm"
            onClick={async () => {
              try {
                await navigator.clipboard.writeText(settings.ics_url)
                toast.success('Feed URL copied.')
              } catch {
                toast.error('Could not copy. Select the text and copy it manually.')
              }
            }}
          >
            Copy
          </button>
          <button
            type="button"
            className="btn btn-danger btn-sm"
            onClick={() => {
              if (
                !window.confirm(
                  'Regenerate the calendar secret? Outlook will stop updating until you resubscribe with the new URL.',
                )
              )
                return
              void act(api.settings.regenerateIcsToken, 'New feed URL generated.')
            }}
          >
            Regenerate
          </button>
        </div>
        <details className="mt-3 text-xs text-ink-2">
          <summary className="cursor-pointer font-medium text-navy">
            How to add this in Outlook
          </summary>
          <ol className="mt-2 list-decimal space-y-1 pl-5 text-ink-3">
            <li>In Outlook on the web, open <strong>Calendar</strong>.</li>
            <li>
              Select <strong>Add calendar</strong> &rsaquo;{' '}
              <strong>Subscribe from web</strong>.
            </li>
            <li>Paste the URL above, give it a name such as &ldquo;Docket&rdquo;, and import.</li>
            <li>
              In desktop Outlook the equivalent is <strong>Account Settings</strong> &rsaquo;{' '}
              <strong>Internet Calendars</strong> &rsaquo; <strong>New</strong>.
            </li>
          </ol>
          <p className="mt-2 text-ink-3">
            Outlook decides its own refresh interval; the feed asks for hourly. It is
            one-way: changes made in Outlook do not come back to Docket.
          </p>
        </details>
      </Section>

      {/* ------------------------------------------------ connected calendars --*/}
      <Section
        title="Connected calendars"
        description="Read-only, and the opposite direction to the feed above: these bring your meetings into Docket's dashboard. Docket never writes to them."
      >
        <CalendarFeeds />
        <details className="mt-3 text-xs text-ink-2">
          <summary className="cursor-pointer font-medium text-navy">
            Where to find the URL
          </summary>
          <p className="mt-2">
            <strong>Outlook</strong>: Settings &rsaquo; Calendar &rsaquo; Shared calendars
            &rsaquo; Publish a calendar. Choose <strong>Can view all details</strong> —
            availability-only publishes busy blocks with no titles, which is not much use
            on a dashboard. Copy the <strong>ICS</strong> link, not the HTML one.
          </p>
          <p className="mt-2">
            <strong>Google</strong>: Settings &rsaquo; the calendar &rsaquo; Integrate
            calendar &rsaquo; <strong>Secret address in iCal format</strong>.
          </p>
          <p className="mt-2 text-ink-3">
            Both links are credentials: anyone holding one can read that calendar without
            signing in. Docket stores them server-side and never sends them back to the
            browser. If one leaks, unpublish and republish to invalidate it.
          </p>
          <p className="mt-2 text-ink-3">
            Published feeds are refreshed by their provider on its own schedule, often a
            few hours behind. A meeting added this morning may not appear immediately.
          </p>
        </details>
      </Section>

      {/* ----------------------------------------------------------- tree --*/}
      <Section
        title="Categories and subcategories"
        description="Items always live in a subcategory. Archiving hides one without touching its history; deleting is only possible once it is empty."
      >
        <div className="flex flex-col gap-3">
          {categories.map((category) => (
            <div key={category.id} className="rounded-md border border-line p-2">
              <div className="flex flex-wrap items-center gap-2">
                <input
                  className="field flex-1 min-w-40"
                  defaultValue={category.name}
                  onBlur={(event) => {
                    const name = event.target.value.trim()
                    if (name && name !== category.name)
                      void act(() => api.categories.update(category.id, { name }), 'Renamed.')
                  }}
                  aria-label={`Rename ${category.name}`}
                />
                <input
                  type="color"
                  className="h-8 w-10 cursor-pointer rounded-sm border border-line-strong"
                  // Shows the colour the dashboard is actually painting, not
                  // a fixed navy that would contradict it.
                  value={effectiveCategoryColor(category.color, category.id)}
                  onChange={(event) =>
                    void act(
                      () => api.categories.update(category.id, { color: event.target.value }),
                      'Colour updated.',
                    )
                  }
                  aria-label={`Colour for ${category.name}`}
                />
                <button
                  type="button"
                  className="btn btn-neutral btn-sm"
                  onClick={() =>
                    void act(
                      () =>
                        api.categories.update(category.id, {
                          is_archived: !category.is_archived,
                        }),
                      category.is_archived ? 'Restored.' : 'Archived.',
                    )
                  }
                >
                  {category.is_archived ? 'Unarchive' : 'Archive'}
                </button>
                <button
                  type="button"
                  className="btn btn-danger btn-sm"
                  onClick={() => void act(() => api.categories.remove(category.id), 'Deleted.')}
                >
                  Delete
                </button>
              </div>

              <ul className="mt-2 flex flex-col gap-1 pl-4">
                {category.subcategories.map((sub) => (
                  <li key={sub.id} className="flex flex-wrap items-center gap-2">
                    <input
                      className="field w-40 min-w-32 py-1 text-sm"
                      defaultValue={sub.name}
                      onBlur={(event) => {
                        const name = event.target.value.trim()
                        if (name && name !== sub.name)
                          void act(() => api.subcategories.update(sub.id, { name }), 'Renamed.')
                      }}
                      aria-label={`Rename ${sub.name}`}
                    />
                    <input
                      className="field flex-1 min-w-40 py-1 text-sm"
                      defaultValue={sub.status_note ?? ''}
                      placeholder="Status note (optional)"
                      maxLength={200}
                      onBlur={(event) => {
                        // Blank clears it; the server stores null and the
                        // heading then shows nothing.
                        const next = event.target.value.trim()
                        if (next !== (sub.status_note ?? ''))
                          void act(
                            () => api.subcategories.update(sub.id, { status_note: next || null }),
                            next ? 'Status note saved.' : 'Status note cleared.',
                          )
                      }}
                      aria-label={`Status note for ${sub.name}`}
                    />
                    <span className="text-xs text-ink-3">
                      {sub.open_item_count} open
                    </span>
                    <button
                      type="button"
                      className="text-xs font-medium text-navy hover:underline"
                      onClick={() =>
                        void act(
                          () =>
                            api.subcategories.update(sub.id, { is_archived: !sub.is_archived }),
                          sub.is_archived ? 'Restored.' : 'Archived.',
                        )
                      }
                    >
                      {sub.is_archived ? 'Unarchive' : 'Archive'}
                    </button>
                    <button
                      type="button"
                      className="text-xs font-medium text-crimson hover:underline"
                      onClick={() =>
                        void act(() => api.subcategories.remove(sub.id), 'Deleted.')
                      }
                    >
                      Delete
                    </button>
                  </li>
                ))}
              </ul>

              <form
                className="mt-2 flex items-center gap-2 pl-4"
                onSubmit={(event) => {
                  event.preventDefault()
                  const input = event.currentTarget.elements.namedItem(
                    'sub',
                  ) as HTMLInputElement
                  const name = input.value.trim()
                  if (!name) return
                  input.value = ''
                  void act(
                    () => api.subcategories.create(category.id, name),
                    'Subcategory added.',
                  )
                }}
              >
                <input
                  name="sub"
                  className="field w-44 py-1 text-sm"
                  placeholder="New subcategory"
                  aria-label={`New subcategory in ${category.name}`}
                />
                <button type="submit" className="btn btn-neutral btn-sm">
                  Add
                </button>
              </form>
            </div>
          ))}

          <form
            className="flex items-center gap-2"
            onSubmit={(event) => {
              event.preventDefault()
              const input = event.currentTarget.elements.namedItem('cat') as HTMLInputElement
              const name = input.value.trim()
              if (!name) return
              input.value = ''
              void act(() => api.categories.create(name), 'Category added.')
            }}
          >
            <input
              name="cat"
              className="field w-56"
              placeholder="New category"
              aria-label="New category"
            />
            <button type="submit" className="btn btn-primary btn-sm">
              Add category
            </button>
          </form>
        </div>
      </Section>

      {/* ----------------------------------------------------------- tags --*/}
      <Section
        title="Tags"
        description="Cross-cutting labels. Deleting a tag only removes the label; the items keep everything else."
      >
        <div className="flex flex-col gap-2">
          {tags.length === 0 && <p className="text-sm text-ink-3">No tags yet.</p>}
          {tags.map((tag) => (
            <div key={tag.id} className="flex flex-wrap items-center gap-2">
              <input
                className="field w-44 py-1 text-sm"
                defaultValue={tag.name}
                onBlur={(event) => {
                  const name = event.target.value.trim()
                  if (name && name !== tag.name)
                    void act(() => api.tags.update(tag.id, { name }), 'Renamed.')
                }}
                aria-label={`Rename ${tag.name}`}
              />
              <input
                type="color"
                className="h-7 w-9 cursor-pointer rounded-sm border border-line-strong"
                value={tag.color ?? '#6B7280'}
                onChange={(event) =>
                  void act(() => api.tags.update(tag.id, { color: event.target.value }), 'Updated.')
                }
                aria-label={`Colour for ${tag.name}`}
              />
              <span className="text-xs text-ink-3">
                {tag.item_count} {pluralize(tag.item_count, 'item')}
              </span>
              <button
                type="button"
                className="text-xs font-medium text-crimson hover:underline"
                onClick={() => {
                  if (!window.confirm(`Remove the tag "${tag.name}" from every item?`)) return
                  void act(() => api.tags.remove(tag.id), 'Tag deleted.')
                }}
              >
                Delete
              </button>
            </div>
          ))}

          {tags.length > 1 && (
            <div className="mt-2 flex flex-wrap items-end gap-2 border-t border-line pt-3">
              <label className="text-xs font-medium text-ink-2">
                Merge
                <select
                  className="field mt-1 w-40"
                  value={mergeSource}
                  onChange={(event) => setMergeSource(event.target.value)}
                >
                  <option value="">choose...</option>
                  {tags.map((tag) => (
                    <option key={tag.id} value={tag.id}>
                      {tag.name}
                    </option>
                  ))}
                </select>
              </label>
              <label className="text-xs font-medium text-ink-2">
                into
                <select
                  className="field mt-1 w-40"
                  value={mergeTarget}
                  onChange={(event) => setMergeTarget(event.target.value)}
                >
                  <option value="">choose...</option>
                  {tags.map((tag) => (
                    <option key={tag.id} value={tag.id}>
                      {tag.name}
                    </option>
                  ))}
                </select>
              </label>
              <button
                type="button"
                className="btn btn-neutral btn-sm"
                disabled={!mergeSource || !mergeTarget || mergeSource === mergeTarget}
                onClick={() => {
                  void act(
                    () => api.tags.merge(Number(mergeSource), Number(mergeTarget)),
                    'Tags merged.',
                  )
                  setMergeSource('')
                  setMergeTarget('')
                }}
              >
                Merge
              </button>
              <p className="w-full text-xs text-ink-3">
                Merging moves every item from the first tag to the second, then deletes the
                first. Renaming two tags to the same thing is not possible, so this is how a
                duplicate gets fixed.
              </p>
            </div>
          )}
        </div>
      </Section>

      {/* -------------------------------------------------------- invoice --*/}
      <Section
        title="Invoices"
        description="What every invoice prints besides the work itself. These are copied onto an invoice as it is raised, so changing one here leaves everything already sent exactly as it was sent."
      >
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          {INVOICE_FIELDS.map((field) => (
            <label
              key={field.key}
              className={`text-xs font-semibold text-ink-2 ${field.multiline ? 'sm:col-span-2' : ''}`}
            >
              {field.label}
              {field.multiline ? (
                <textarea
                  className="field mt-1 text-sm"
                  rows={2}
                  defaultValue={String(settings[field.key] ?? '')}
                  onBlur={(event) => {
                    const value = event.target.value
                    if (value !== settings[field.key])
                      void save({ [field.key]: value } as Partial<AppSettings>)
                  }}
                />
              ) : (
                <input
                  className="field mt-1 text-sm"
                  defaultValue={String(settings[field.key] ?? '')}
                  onBlur={(event) => {
                    const value = event.target.value
                    if (value !== settings[field.key])
                      void save({ [field.key]: value } as Partial<AppSettings>)
                  }}
                />
              )}
              {field.hint && <span className="mt-0.5 block font-normal text-ink-3">{field.hint}</span>}
            </label>
          ))}
        </div>
      </Section>

      {/* --------------------------------------------------------- export --*/}
      <Section
        title="Export"
        description="Everything: the tree, every item, note, checklist, tag, file record and the full activity log."
      >
        <a href="/api/settings/export" className="btn btn-neutral btn-sm" download>
          Download JSON
        </a>
      </Section>

      {/* ------------------------------------------------------- password --*/}
      <Section
        title="Password"
        description="Changing it signs out every other device. This one stays signed in."
      >
        <form
          className="flex max-w-sm flex-col gap-2"
          onSubmit={async (event) => {
            event.preventDefault()
            if (newPassword !== confirmPassword) {
              toast.error('The new passwords do not match.')
              return
            }
            try {
              const result = await api.auth.changePassword(currentPassword, newPassword)
              setCurrentPassword('')
              setNewPassword('')
              setConfirmPassword('')
              toast.success(result.message)
            } catch (caught) {
              toast.error(
                caught instanceof ApiError ? caught.message : 'Could not change the password.',
              )
            }
          }}
        >
          <input
            type="password"
            className="field"
            placeholder="Current password"
            autoComplete="current-password"
            value={currentPassword}
            onChange={(event) => setCurrentPassword(event.target.value)}
            aria-label="Current password"
          />
          <input
            type="password"
            className="field"
            placeholder="New password (at least 8 characters)"
            autoComplete="new-password"
            value={newPassword}
            onChange={(event) => setNewPassword(event.target.value)}
            aria-label="New password"
          />
          <input
            type="password"
            className="field"
            placeholder="Confirm new password"
            autoComplete="new-password"
            value={confirmPassword}
            onChange={(event) => setConfirmPassword(event.target.value)}
            aria-label="Confirm new password"
          />
          <button
            type="submit"
            className="btn btn-primary btn-sm self-start"
            disabled={
              !currentPassword || newPassword.length < 8 || newPassword !== confirmPassword
            }
          >
            Change password
          </button>
        </form>
      </Section>
    </div>
  )
}
